import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { Hidden } from 'ech-components/src/helpers';
import Container from 'ech-components/src/helpers/Container';
import { Link, BundesBanner } from 'ech-components/src/primitives';
import {
  colors,
  mixins,
  spaces,
} from 'ech-components/src/core/styles/theme/default';
import Spacer from 'ech-components/src/helpers/Spacer';
import { Grid, GridItem } from 'ech-components/src/helpers';
import LanguageSwitcher from '@components/Language-Switcher';
import { createRoutingURI, getRootSlug } from '@scripts/utils/domain';
import { useIntl } from 'react-intl';
import { Fade } from 'react-awesome-reveal';
import { CampaignNavigationInterface } from '~/queries/useNavigationQuery';
import { Logo } from 'ech-components/src/primitives';
import { getRootTranslation } from '../../../gatsby-helper';
import { ImageInterface } from 'ech-components/src/types';

const StyledNav = styled.div(
  ({ isVisible }: { isVisible?: boolean }) => css`
    width: 100%;
    height: 100%;
    position: relative;
    top: 0;
    left: 0;
    flex-basis: 100%;
    display: ${isVisible ? 'block' : 'none'};

    ${mixins.responsiveStyles({
      'padding-top': { zero: 24, medium: 32, large: 40 },
      'padding-bottom': { zero: 24, medium: 32, large: 40 },
    })}
  `,
);

const StyledNavWrap = styled(Container)`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;

  ${mixins.MQMax('large')} {
    flex-flow: column nowrap;
  }

  ${mixins.MQMax('wide')} {
    padding: 0;
  }

  ${mixins.MQMax('wide')} {
    padding: 0;
  }

  ${mixins.MQ('ultra')} {
    padding-left: ${rem(46)};
  }
`;

const StyledMainNav = styled.div`
  flex: 1;

  ${mixins.responsiveStyles({
    'padding-right': {
      zero: 0,
      large: 36,
    },
  })}
`;

const StyledSideNav = styled.aside`
  display: flex;
  flex: 0 1 100%;
  flex-direction: column;

  ${mixins.MQ('large')} {
    flex: 0 0 ${rem(246)};
    width: ${rem(246)};
  }
`;

const StyledNavTitle = styled.strong`
  display: block;
  margin-bottom: ${rem(spaces.gamma)};
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.075rem;
  color: ${colors.secondarySoftPurple200};
  font-size: ${rem(12)};
  line-height: ${rem(16)};

  ${mixins.MQ('large')} {
    margin-bottom: ${rem(spaces.epsilon)};
  }
`;

const StyledEmphasizedLink = styled(Link)`
  display: inline-flex;
  min-height: ${rem(56)};
  border: ${rem(1)} solid ${colors.secondarySoftPurple200};
`;

const StyledRowBoxLarge = styled.div`
  display: block;

  nav > div {
    display: grid;
    margin: 0;
    width: 100%;

    ${mixins.responsiveStyles({
      'grid-template-columns': {
        zero: 'repeat(2, 1fr)',
        medium: 'repeat(3, 1fr)',
        large: 'repeat(4, 1fr)',
        ultra: 'repeat(5, 1fr)',
      },
      gap: {
        zero: rem(spaces.alpha),
        large: rem(spaces.gamma),
        ultra: rem(spaces.beta),
      },
    })}

    > div {
      margin: 0;

      > div {
        height: 100%;
      }
    }

    a {
      width: 100%;
      height: 100%;
    }
  }
`;

const StyledRowBox = styled.div`
  display: block;

  nav > div {
    display: grid;
    margin: 0;
    width: 100%;

    ${mixins.responsiveStyles({
      'grid-template-columns': {
        zero: 'repeat(1, 1fr)',
        small: 'repeat(2, 1fr)',
        large: 'repeat(3, 1fr)',
        ultra: 'repeat(4, 1fr)',
      },
      gap: {
        zero: rem(spaces.alpha),
        large: rem(spaces.gamma),
        ultra: rem(spaces.epsilon),
      },
    })}

    > div {
      margin: 0;

      > div {
        height: 100%;
      }
    }

    a {
      width: 100%;
      height: 100%;
    }
  }
`;

const StyledMetaNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${rem(-5)};
  margin-left: ${rem(-5)};
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  padding: ${rem(5)};
  margin-bottom: 0 !important;
  cursor: pointer;
  color: ${colors.white};

  &:hover,
  &:focus {
    color: ${colors.secondarySoftPurple200};
  }

  ${mixins.MQ('large')} {
    margin-bottom: ${rem(spaces.alpha)};
    font-size: ${rem(18)};
    line-height: ${rem(28)};
  }
`;

const StyledLanguageSwitcher = styled.div`
  margin-left: ${rem(-5)};
`;

const StyledBundesBanner = styled(BundesBanner)`
  max-width: ${rem(205)};
  margin-bottom: ${rem(30)};
`;

const StyledECHLogo = styled.div`
  max-width: ${rem(196)};

  a {
    display: flex;
    width: inherit;

    svg {
      width: inherit;
    }
  }
`;

const imageSizes = {
  0: [235, 160],
  480: [390, 238],
  768: [342, 214],
  1024: [228, 296],
  1280: [176, 245],
  1440: [190, 258],
};

const Navigation = ({ isVisible, node, langObj }: NavigationProps) => {
  const intl = useIntl();
  const servicesLabel = intl.formatMessage({
    id: 'page.navigation.services',
  });
  const echLogoName = intl.formatMessage({
    id: 'nav.echLogo.name',
  });
  const echLogoSlug = intl.formatMessage({
    id: 'nav.echLogo.slug',
  });

  return (
    <StyledNav
      role="navigation"
      aria-label={intl.formatMessage({
        id: 'nav.menu.title',
      })}
      id="main-menu-content"
      isVisible={isVisible ? true : false}
    >
      <StyledNavWrap>
        <StyledMainNav>
          <StyledRowBoxLarge>
            <StyledNavTitle id={node.groupName1}>
              {node.groupName1}
            </StyledNavTitle>
            <nav role="menu" aria-labelledby={node.groupName1}>
              <Grid columns={{ zero: 12 }}>
                {node.groupNavigation1?.map(
                  (
                    item: {
                      slug: string;
                      navigationTitle: string;
                      teaserImage: ImageInterface;
                    },
                    rowIndex: number,
                  ) => {
                    if (item.teaserImage) {
                      item.teaserImage.imageSizes = imageSizes;
                    }

                    return (
                      <GridItem
                        size={{ zero: 6, large: 3 }}
                        push={{ zero: 0 }}
                        key={rowIndex}
                      >
                        <Fade delay={rowIndex * 60} triggerOnce>
                          <Link
                            role="menuitem"
                            aria-labelledby={`menuitem${item.navigationTitle}Label`}
                            linkText={item.navigationTitle}
                            link={{
                              href: createRoutingURI(
                                getRootSlug(item, node.node_locale),
                              ),
                              title: `${item.navigationTitle}`,
                            }}
                            variant="emphasizedImageNavigation"
                            backgroundImage={item.teaserImage}
                          />
                        </Fade>
                      </GridItem>
                    );
                  },
                )}
              </Grid>
              <Spacer space="mega" />
            </nav>
          </StyledRowBoxLarge>
          <StyledRowBox>
            <StyledNavTitle id={node.groupName2}>
              {node.groupName2}
            </StyledNavTitle>
            <nav role="menu" aria-labelledby={node.groupName2}>
              <Grid columns={{ zero: 12 }}>
                {node.groupNavigation2?.map(
                  (
                    item: {
                      slug: string;
                      navigationTitle: string;
                    },
                    rowIndex: number,
                  ) => {
                    return (
                      <GridItem
                        size={{ zero: 6, large: 4, wide: 3 }}
                        push={{ zero: 0 }}
                        key={rowIndex}
                      >
                        <Fade delay={360 + rowIndex * 60} triggerOnce>
                          <StyledEmphasizedLink
                            role="menuitem"
                            linkText={item.navigationTitle}
                            link={{
                              href: createRoutingURI(
                                getRootSlug(item, node.node_locale),
                              ),
                              title: `${item.navigationTitle}`,
                            }}
                            variant="emphasizedSmall"
                          />
                        </Fade>
                      </GridItem>
                    );
                  },
                )}
              </Grid>
            </nav>
            <Spacer space="giga" />
          </StyledRowBox>
          <Hidden below="large">
            <StyledLanguageSwitcher>
              <LanguageSwitcher langObj={langObj} />
            </StyledLanguageSwitcher>
          </Hidden>
        </StyledMainNav>
        <StyledSideNav>
          <StyledRowBox>
            <StyledNavTitle id={servicesLabel}>{servicesLabel}</StyledNavTitle>
            <StyledMetaNav role="menu" aria-labelledby={servicesLabel}>
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.news',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('news', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.news',
                  }),
                }}
                color={'light'}
              />
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.events',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('events', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.events',
                  }),
                }}
                color={'light'}
              />
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.consultations',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('consultations', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.consultations',
                  }),
                }}
                color={'light'}
              />
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.tools',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('tools', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.tools',
                  }),
                }}
                color={'light'}
              />
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.fundings',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('fundings', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.fundings',
                  }),
                }}
                color={'light'}
              />
              <StyledLink
                role="menuitem"
                linkText={intl.formatMessage({
                  id: 'page.navigation.stories',
                })}
                link={{
                  href: createRoutingURI({
                    root: getRootTranslation('stories', intl.locale),
                    slug: '',
                    locale: intl.locale,
                  }),
                  title: intl.formatMessage({
                    id: 'page.navigation.stories',
                  }),
                }}
                color={'light'}
              />
            </StyledMetaNav>
            <Spacer space="delta" />
            <Hidden below="large">
              <Spacer space="delta" />
            </Hidden>
          </StyledRowBox>
          <StyledRowBox>
            <StyledNavTitle id={node.groupName3}>
              {node.groupName3}
            </StyledNavTitle>
            <StyledMetaNav role="menu" aria-labelledby={node.groupName3}>
              {node.groupNavigation3.map(
                (
                  item: {
                    __typename: string;
                    slug: string;
                    navigationTitle: string;
                  },
                  rowIndex: number,
                ) => {
                  return (
                    <StyledLink
                      role="menuitem"
                      aria-labelledby={`menuitem${item.navigationTitle}Label`}
                      linkText={item.navigationTitle}
                      link={{
                        href: createRoutingURI(
                          getRootSlug(item, langObj.actual),
                        ),
                        title: `${item.navigationTitle}`,
                      }}
                      color={'light'}
                      key={rowIndex}
                    />
                  );
                },
              )}
            </StyledMetaNav>
            <Hidden above="large">
              <Spacer space="delta" />
            </Hidden>
          </StyledRowBox>
          <Hidden above="large">
            <div>
              <LanguageSwitcher langObj={langObj} borders />
            </div>
          </Hidden>
          <StyledRowBox>
            <Spacer space="mega" />
            <StyledBundesBanner lang={langObj?.actual || 'de'} />
            <StyledECHLogo>
              <Logo
                isLight
                name={echLogoName}
                slug={echLogoSlug}
                locale={langObj?.actual || 'de'}
              />
            </StyledECHLogo>
          </StyledRowBox>
        </StyledSideNav>
      </StyledNavWrap>
    </StyledNav>
  );
};

export type NavigationProps = {
  isVisible?: boolean;
  node: CampaignNavigationInterface;
  langObj: {
    actual: string;
    switcher: object[];
  };
};

export default Navigation;
